import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';

// Define error categories for better error handling
export enum ErrorCategory {
    Network = 'Network Error',
    Authentication = 'Authentication Error',
    Authorization = 'Authorization Error',
    Validation = 'Validation Error',
    Server = 'Server Error',
    Client = 'Client Error',
    Unknown = 'Unknown Error'
}

interface ErrorDetails {
    category: ErrorCategory;
    message: string;
    timestamp: string;
    componentStack?: string;
}

interface Props {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface State {
    hasError: boolean;
    errorDetails?: ErrorDetails;
    recoveryAttempts: number;
}

// Maximum number of automatic recovery attempts
const MAX_RECOVERY_ATTEMPTS = 3;

// Function to categorize errors
function categorizeError(error: Error): ErrorCategory {
    if (error.name === 'NetworkError' || error.message.includes('network')) {
        return ErrorCategory.Network;
    }
    if (error.message.includes('authentication') || error.message.includes('login')) {
        return ErrorCategory.Authentication;
    }
    if (error.message.includes('permission') || error.message.includes('unauthorized')) {
        return ErrorCategory.Authorization;
    }
    if (error.message.includes('validation') || error.message.includes('invalid')) {
        return ErrorCategory.Validation;
    }
    if (error.message.includes('server')) {
        return ErrorCategory.Server;
    }
    return ErrorCategory.Unknown;
}

export class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { 
            hasError: false,
            recoveryAttempts: 0
        };
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError(error: Error): State {
        const category = categorizeError(error);
        return {
            hasError: true,
            errorDetails: {
                category,
                message: error.message,
                timestamp: new Date().toISOString(),
            },
            recoveryAttempts: 0
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log error details
        const errorDetails: ErrorDetails = {
            category: categorizeError(error),
            message: error.message,
            timestamp: new Date().toISOString(),
            componentStack: errorInfo.componentStack
        };

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', {
                error,
                errorInfo,
                errorDetails
            });
        }

        // Here you could send error details to your error tracking service
        // Example: sendToErrorTrackingService(errorDetails);

        this.setState({ errorDetails });

        // Attempt automatic recovery for certain error categories
        if (
            this.state.recoveryAttempts < MAX_RECOVERY_ATTEMPTS && 
            [ErrorCategory.Network, ErrorCategory.Server].includes(errorDetails.category)
        ) {
            setTimeout(() => {
                this.handleRetry();
            }, 3000); // Wait 3 seconds before attempting recovery
        }
    }

    handleRetry() {
        this.setState(prevState => ({
            hasError: false,
            recoveryAttempts: prevState.recoveryAttempts + 1
        }));
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return (
                <ErrorFallback 
                    errorDetails={this.state.errorDetails}
                    onRetry={this.handleRetry}
                />
            );
        }

        return this.props.children;
    }
}

function ErrorFallback({ errorDetails, onRetry }: { errorDetails?: ErrorDetails; onRetry: () => void }) {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const getErrorMessage = (category: ErrorCategory, message: string): string => {
        switch (category) {
            case ErrorCategory.Network:
                return "Unable to connect to the server. Please check your internet connection.";
            case ErrorCategory.Authentication:
                return "Your session has expired. Please log in again.";
            case ErrorCategory.Authorization:
                return "You don't have permission to access this resource.";
            case ErrorCategory.Validation:
                return "Invalid input or data format.";
            case ErrorCategory.Server:
                return "The server encountered an error. Please try again later.";
            default:
                return message || "An unexpected error occurred.";
        }
    };

    const handleHomeClick = () => {
        showToast('Navigating to home page...', 'info');
        navigate('/');
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
            <div className="max-w-max mx-auto">
                <main className="sm:flex">
                    <p className="text-4xl font-extrabold text-blue-600 sm:text-5xl">Oops!</p>
                    <div className="sm:ml-6">
                        <div className="sm:border-l sm:border-gray-200 sm:pl-6">
                            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight sm:text-5xl">
                                Something went wrong
                            </h1>
                            <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mt-2">
                                {errorDetails?.category || ErrorCategory.Unknown}
                            </h2>
                            <p className="mt-3 text-base text-gray-500 dark:text-gray-400">
                                {errorDetails ? getErrorMessage(errorDetails.category, errorDetails.message) : 'An unexpected error occurred'}
                            </p>
                            {errorDetails?.timestamp && (
                                <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                                    Error occurred at: {new Date(errorDetails.timestamp).toLocaleString()}
                                </p>
                            )}
                        </div>
                        <div className="mt-6 flex space-x-3 sm:border-l sm:border-transparent sm:pl-6">
                            <button
                                onClick={onRetry}
                                className="btn btn-primary" // Using btn-primary for consistent button style
                            >
                                Reload page
                            </button>
                            <button
                                onClick={handleHomeClick}
                                className="btn btn-ghost" // Using btn-ghost for consistent button style
                            >
                                Go home
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
